import * as RadixDialog from '@radix-ui/react-dialog';
import { VisuallyHidden as RadixVisuallyHidden } from '@radix-ui/react-visually-hidden';
import { AnimatePresence, motion, type Variants } from 'framer-motion';
import React, { memo, useEffect, useRef, type ReactNode } from 'react';
import { IconBadge, type IconBadgeType } from './IconBadge';
import { IconButton } from './IconButton';
import { Button, type ButtonProps } from '~/components/ui/Button';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';

export { Close as DialogClose } from '@radix-ui/react-dialog';

const transition = {
  duration: 0.15,
  ease: cubicEasingFn,
};

export const dialogBackdropVariants = {
  closed: {
    opacity: 0,
    transition,
  },
  open: {
    opacity: 1,
    transition,
  },
} satisfies Variants;

export const dialogVariants = {
  closed: {
    x: '-50%',
    y: '-40%',
    scale: 0.96,
    opacity: 0,
    transition,
  },
  open: {
    x: '-50%',
    y: '-50%',
    scale: 1,
    opacity: 1,
    transition,
  },
} satisfies Variants;

interface DialogHeaderProps {
  title?: ReactNode;
  description?: ReactNode;
  badge?: IconBadgeType;
  unstyled?: boolean;
  className?: string;
  onBack?: VoidFunction;
  noBorder?: boolean;
  wrapperClassName?: string;
}

export const DialogHeader = memo(
  ({
    title,
    description,
    badge,
    onBack,
    unstyled = false,
    className,
    noBorder = false,
    wrapperClassName,
  }: DialogHeaderProps) => {
    return (
      <div
        className={classNames(
          unstyled
            ? ''
            : 'flex flex-col gap-6 items-start min-w-0 pt-6 pl-6 pr-12 bg-bolt-ds-surfaceAltDark overflow-hidden',
          !noBorder && !unstyled ? 'border-b border-bolt-ds-borderOutline pb-6' : '',
          className,
        )}
      >
        {badge && <IconBadge type={badge} />}

        <div className="flex items-start gap-2 max-w-full overflow-hidden">
          {onBack && <IconButton icon="i-heroicons:arrow-left-solid" size="md" variant="ghost" onClick={onBack} />}

          <div className={classNames('flex flex-col gap-1 overflow-hidden', onBack ? 'pt-1' : '', wrapperClassName)}>
            {title && (
              <RadixDialog.DialogTitle className="text-lg font-semibold text-bolt-ds-textPrimary truncate">
                {title}
              </RadixDialog.DialogTitle>
            )}

            {description && <div className="text-base text-bolt-ds-textSecondary">{description}</div>}
          </div>
        </div>
      </div>
    );
  },
);

interface DialogImageHeaderProps extends Pick<DialogHeaderProps, 'title' | 'description'> {
  src: string;
}

export const DialogImageHeader = memo(({ src, title, description }: DialogImageHeaderProps) => {
  return (
    <div>
      <div className="w-full h-50 bg-bolt-ds-surfaceAltDark bg-cover" style={{ backgroundImage: `url(${src})` }}></div>
      <div className="flex flex-col gap-4 p-6">
        {title ? (
          <RadixDialog.DialogTitle className="text-lg font-semibold text-bolt-ds-textPrimary">
            {title}
          </RadixDialog.DialogTitle>
        ) : (
          <RadixVisuallyHidden>
            <RadixDialog.DialogTitle>{src}</RadixDialog.DialogTitle>
          </RadixVisuallyHidden>
        )}

        {description && <p className="text-base text-bolt-ds-textSecondary">{description}</p>}
      </div>
    </div>
  );
});

interface DialogContentProps {
  children: ReactNode;
  className?: string;
  unstyled?: boolean;
  noPadding?: boolean;
}

export const DialogContent = memo(
  ({ children, className, unstyled = false, noPadding = false, ...props }: DialogContentProps) => {
    return (
      <RadixDialog.Description
        className={classNames(
          {
            'text-bolt-ds-textSecondary text-sm': !unstyled,
            'p-6': !unstyled && !noPadding,
          },
          className,
        )}
        asChild
        {...props}
      >
        <div>{children}</div>
      </RadixDialog.Description>
    );
  },
);

interface DialogActionsProps {
  children: ReactNode;
  orientation?: 'horizontal' | 'vertical';
  actionSize?: 'compact' | 'fullwidth';
  className?: string;
}

export const DialogActions = memo(
  ({ children, orientation = 'horizontal', actionSize = 'compact', className }: DialogActionsProps) => {
    return (
      <div
        className={classNames(
          'p-4 flex gap-2',
          {
            'justify-end': orientation === 'horizontal',
            'flex-col': orientation === 'vertical',
            '[&>*]:flex-1': orientation === 'horizontal' && actionSize === 'fullwidth',
          },
          className,
        )}
      >
        {children}
      </div>
    );
  },
);

export const DialogButton = memo((props: ButtonProps) => {
  return (
    <Button {...props} className={classNames('px-4 leading-none focus:outline-none gap-2', props.className)}>
      {props.children}
    </Button>
  );
});

export interface DialogProps {
  open: boolean;
  showOverflow?: boolean;
  children: ReactNode | ReactNode[];
  contentClassName?: string;
  backdropClassName?: string;
  closeButtonClassName?: string;
  unstyled?: boolean;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  width?: string | number;
  height?: string | number;
  maxWidth?: string | number;
  maxHeight?: string | number;
  onClose?: () => void;
  portalContainer?: Element;

  'data-testid'?: string;

  /**
   * The modality of the dialog. When set to `true`, interaction with outside elements will
   * be disabled and only dialog content will be visible to screen readers.
   *
   * If you provide a `portalContainer` it might make sense to disable the modality.
   */
  modal?: boolean;
}

export const Dialog = memo<DialogProps>(
  ({
    contentClassName,
    backdropClassName,
    open = false,
    width = '90vw',
    height,
    showOverflow = false,
    maxWidth,
    maxHeight = '85vh',
    showCloseButton = true,
    closeButtonClassName = '',
    closeOnBackdropClick = true,
    closeOnEscape = true,
    unstyled = false,
    portalContainer,
    children,
    modal,
    onClose,
    'data-testid': dataTestId,
  }) => {
    const onCloseRef = useRef(onClose);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      onCloseRef.current = onClose;
    });

    useEffect(() => {
      if (!open) {
        return () => {
          // noop
        };
      }

      const onKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && closeOnEscape) {
          onCloseRef.current?.();
        }
      };

      window.addEventListener('keydown', onKeydown);

      return () => {
        window.removeEventListener('keydown', onKeydown);
      };
    }, [open, closeOnEscape]);

    return (
      <RadixDialog.Root open={open} modal={modal}>
        <AnimatePresence>
          {open && (
            <RadixDialog.Portal forceMount container={portalContainer}>
              <RadixDialog.Overlay asChild>
                <motion.div
                  className={classNames('bg-black/50 fixed inset-0 z-dialog', backdropClassName)}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  variants={dialogBackdropVariants}
                />
              </RadixDialog.Overlay>
              <RadixDialog.Content
                ref={contentRef}
                asChild
                aria-describedby={undefined}
                onPointerDownOutside={closeOnBackdropClick ? onClose : undefined}
                data-testid={dataTestId}
              >
                <motion.div
                  className={classNames(
                    'fixed top-[50%] left-[50%] z-max -translate-x-1/2 -translate-y-1/2 focus:outline-none',
                    unstyled
                      ? undefined
                      : 'border border-bolt-ds-borderOutline rounded-xl bg-bolt-ds-surfaceAltDark shadow-lg',
                    {
                      'overflow-hidden': !showOverflow,
                    },
                    contentClassName,
                  )}
                  style={{
                    width: typeof width === 'string' ? width : `${width}px`,
                    height: typeof height === 'string' ? height : height === undefined ? undefined : `${height}px`,
                    maxWidth:
                      typeof maxWidth === 'string'
                        ? maxWidth
                        : typeof maxWidth === 'number'
                          ? `${maxWidth}px`
                          : undefined,
                    maxHeight: typeof maxHeight === 'string' ? maxHeight : `${maxHeight}px`,
                  }}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  variants={dialogVariants}
                >
                  {children}
                  {showCloseButton && (
                    <RadixDialog.Close asChild onClick={onClose}>
                      <IconButton
                        size="md"
                        variant="ghost"
                        icon="i-heroicons:x-mark-solid"
                        className={classNames(
                          'absolute top-2 right-2 size-9 justify-center z-layer-3',
                          closeButtonClassName,
                        )}
                        aria-label="Close"
                      />
                    </RadixDialog.Close>
                  )}
                </motion.div>
              </RadixDialog.Content>
            </RadixDialog.Portal>
          )}
        </AnimatePresence>
      </RadixDialog.Root>
    );
  },
);

interface HiddenDialogTitleProps {
  children: ReactNode;
}

export const HiddenDialogTitle = ({ children }: HiddenDialogTitleProps) => {
  return (
    <RadixVisuallyHidden>
      <RadixDialog.DialogTitle>{children}</RadixDialog.DialogTitle>
    </RadixVisuallyHidden>
  );
};
